/**
 * ============================================
 * WISHLIST SYSTEM - Section Compatible
 * ============================================
 */

(function() {
    'use strict';
  
    const CONFIG = {
      storageKey: 'custom_wishlist_section',
      maxItems: 50,
      selectors: {
        btn: '[data-wishlist-btn]',
        count: '[data-wishlist-count]',
        grid: '[data-wishlist-grid]',
        empty: '[data-wishlist-empty]',
        wrapper: '[data-wishlist-wrapper]'
      },
      classes: {
        added: 'is--added',
        loading: 'is--loading',
        success: 'is--success',
        updated: 'is--updated',
        visible: 'is--visible',
        hidden: 'hidden'
      }
    };
  
    class WishlistSystem {
      constructor() {
        this.items = this.getItems();
        this.init();
      }
  
      init() {
        this.bindEvents();
        this.updateAllUI();
        this.renderAllGrids();
        
        // Multiple init attempts for sections
        if (document.readyState === 'complete') {
          this.forceUpdateAll();
        } else {
          window.addEventListener('load', () => this.forceUpdateAll());
        }
        
        // Re-init on section reload (Shopify customizer)
        document.addEventListener('shopify:section:load', () => {
          this.items = this.getItems();
          this.updateAllUI();
          this.renderAllGrids();
        });
      }
  
      getItems() {
        try {
          const data = localStorage.getItem(CONFIG.storageKey);
          return data ? JSON.parse(data) : [];
        } catch (e) {
          return [];
        }
      }
  
      save() {
        localStorage.setItem(CONFIG.storageKey, JSON.stringify(this.items));
      }
  
      add(productData) {
        const exists = this.items.find(i => i.id === String(productData.id));
        
        if (exists) {
          this.remove(productData.id);
          return 'removed';
        }
  
        if (this.items.length >= CONFIG.maxItems) {
          this.showToast('Wishlist full!', 'error');
          return false;
        }
  
        const item = {
          id: String(productData.id),
          handle: productData.handle,
          title: productData.title,
          url: productData.url,
          image: productData.image,
          price: productData.price,
          variantId: productData.variantId || null,
          addedAt: Date.now()
        };
  
        this.items.unshift(item);
        this.save();
        this.updateAllUI();
        this.renderAllGrids(); // Re-render all grids on page
        this.showToast('Added to wishlist!', 'success');
        this.animateAllCounters();
        
        return 'added';
      }
  
      remove(id) {
        const index = this.items.findIndex(i => i.id === String(id));
        if (index === -1) return false;
  
        this.items.splice(index, 1);
        this.save();
        this.updateAllUI();
        this.renderAllGrids();
        
        return true;
      }
  
      isInWishlist(id) {
        return this.items.some(i => i.id === String(id));
      }
  
      getCount() {
        return this.items.length;
      }
  
      // ==========================================
      // UI UPDATES
      // ==========================================
      
      forceUpdateAll() {
        this.updateAllUI();
        this.renderAllGrids();
        console.log('Wishlist: Force updated, count =', this.getCount());
      }
  
      updateAllUI() {
        const count = this.getCount();
  
        // Update all buttons
        document.querySelectorAll(CONFIG.selectors.btn).forEach(btn => {
          const id = btn.dataset.productId;
          btn.classList.toggle(CONFIG.classes.added, this.isInWishlist(id));
          btn.classList.remove(CONFIG.classes.loading);
        });
  
        // Update all counters
        document.querySelectorAll(CONFIG.selectors.count).forEach(counter => {
          counter.textContent = count;
          
          // Toggle visibility based on count
          if (count === 0) {
            counter.style.display = 'none';
          } else {
            if (
              counter.classList.contains('header-wishlist-count') ||
              counter.classList.contains('cart__wishlist-count')
            ) {
              counter.style.display = 'flex';
            } else {
              counter.style.display = '';
            }
          }
        });
      }
  
      animateAllCounters() {
        document.querySelectorAll(CONFIG.selectors.count).forEach(counter => {
          counter.classList.remove(CONFIG.classes.updated);
          void counter.offsetWidth;
          counter.classList.add(CONFIG.classes.updated);
          
          setTimeout(() => counter.classList.remove(CONFIG.classes.updated), 300);
        });
      }
  
      // ==========================================
      // RENDER GRIDS (Multiple sections support)
      // ==========================================
      
      renderAllGrids() {
        document.querySelectorAll(CONFIG.selectors.grid).forEach(grid => {
          this.renderGrid(grid);
        });
      }
  
      renderGrid(grid) {
        const empty = grid.closest('[data-wishlist-wrapper]')?.querySelector(CONFIG.selectors.empty) 
                     || document.querySelector(CONFIG.selectors.empty);
        const count = this.getCount();
  
        if (count === 0) {
          grid.innerHTML = '';
          if (empty) {
            empty.classList.remove(CONFIG.classes.hidden);
            empty.classList.add(CONFIG.classes.visible);
            setTimeout(() => empty.style.opacity = '1', 10);
          }
          return;
        }
  
        if (empty) {
          empty.classList.add(CONFIG.classes.hidden);
          empty.classList.remove(CONFIG.classes.visible);
        }
  
        grid.innerHTML = this.items.map(item => this.renderThemeCard(item)).join('');
      }
  
      renderThemeCard(item) {
        // Render wishlist items with theme product-card classes
        return `
          <div class="grid__item" data-wishlist-item data-product-id="${item.id}">
            <div class="product-card product-card-style-${this.getCardStyle()}">
              <div class="product-card__wrapper h-full">
                <div class="product-card__image-wrapper product-card__image-wrapper--main-only bg-none">
                  <button class="wishlist-card__remove" data-wishlist-remove data-product-id="${item.id}" aria-label="Remove from wishlist">
                   
                  <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close icon--medium icon--thick" fill="none" viewBox="0 0 24 24">
        <path d="M18.75 5.13496L5.25 18.8544" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
        <path d="M18.75 18.8544L5.25 5.13496" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
      </svg>
                  </button>

                  <a href="${item.url}" aria-label="${this.escapeHtml(item.title)}" class="block product-card__media">
                    <div class="media-wrapper product-card__image product-card__image--main" style="--aspect-ratio: 1;">
                      <img src="${item.image}" alt="${this.escapeHtml(item.title)}" class="motion-reduce" loading="lazy">
                    </div>
                  </a>
                </div>
                <div class="product-card__info text-left">
                  <h3 class="product-card__title text-pcard-title">
                    <a class="reversed-link" href="${item.url}">${this.escapeHtml(item.title)}</a>
                  </h3>
                  <div class="price">
                    <span class="price-item price-item--regular">${item.price}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `;
      }

      getCardStyle() {
        if (window.themeVariables?.settings?.pcard_style) {
          return window.themeVariables.settings.pcard_style;
        }
        return 'standard';
      }
  
      escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      }
  
      // ==========================================
      // EVENTS
      // ==========================================
      
      bindEvents() {
        // Add buttons
        document.addEventListener('click', (e) => {
          const btn = e.target.closest(CONFIG.selectors.btn);
          if (!btn) return;
  
          e.preventDefault();
          if (btn.classList.contains(CONFIG.classes.loading)) return;
  
          btn.classList.add(CONFIG.classes.loading);
  
          const data = {
            id: btn.dataset.productId,
            handle: btn.dataset.productHandle,
            title: btn.dataset.productTitle,
            url: btn.dataset.productUrl,
            image: btn.dataset.productImage,
            price: btn.dataset.productPrice,
            variantId: btn.dataset.variantId
          };
  
          const result = this.add(data);
  
          setTimeout(() => {
            btn.classList.remove(CONFIG.classes.loading);
            if (result === 'added' && btn.classList.contains('wishlist-product-btn')) {
              btn.classList.add(CONFIG.classes.success);
              setTimeout(() => btn.classList.remove(CONFIG.classes.success), 1200);
            }
          }, 200);
        });
  
        // Remove buttons
        document.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-wishlist-remove]');
          if (!btn) return;
  
          e.preventDefault();
          const id = btn.dataset.productId;
          const item = btn.closest('[data-wishlist-item]');
          
          if (item) {
            item.style.opacity = '0';
            item.style.transform = 'scale(0.9)';
            setTimeout(() => this.remove(id), 300);
          } else {
            this.remove(id);
          }
        });
      }
  
      showToast(message, type = 'success') {
        const existing = document.querySelector('.wishlist-toast');
        if (existing) existing.remove();
  
        const toast = document.createElement('div');
        toast.className = `wishlist-toast is--${type}`;
        toast.innerHTML = `
          <span class="wishlist-toast__icon">
            ${type === 'success' 
              ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg>'
              : '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>'
            }
          </span>
          <span class="wishlist-toast__message">${message}</span>
        `;
  
        document.body.appendChild(toast);
        requestAnimationFrame(() => toast.classList.add('is--show'));
  
        setTimeout(() => {
          toast.classList.remove('is--show');
          setTimeout(() => toast.remove(), 300);
        }, 2800);
      }
    }
  
    // Initialize
    function init() {
      window.WishlistSystem = new WishlistSystem();
    }
  
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  
    window.addEventListener('load', () => {
      if (!window.WishlistSystem) init();
    });
  
  })();